import * as angular from 'angular';
import * as _ from 'lodash';

import {
    ActionType, 
    AlarmTrigger,
    ComponentDefinition, 
    Group,
    IAction, 
    IApplicationConfiguration, 
    ITrigger, 
    PeriodReference, 
    QualityIssueTrigger, 
    ReprocessingTaskAction, 
    ScheduleTrigger, 
    TimeUnit, 
    TriggerAction,
    TriggerType
} from '../../models';
import { ILoggerService, IStoreService, ICurrentApplicationService } from '../../services';
import { toggleEditMode, guidEmpty } from '../../common/';
import { TriggerActionType } from './TriggerOrAction';
import { IRootScope } from '../../IRootScope';
import { StoreElement as SE, ITriggerStoreService, IActionStoreService, ITriggerActionStoreService, Store } from '../../stateManagement';
import { SendMailAction } from '../../models/triggerActions/SendMailAction';
import { TriggerActionSelectedItem } from './TriggerActionSelectedItem';

class TriggerActionStateController implements angular.IComponentController, angular.IOnDestroy {
    actions: IAction[];
    formController: angular.IFormController;
    groups: Group[];
    isEditMode: boolean;
    isLoading: boolean;
    loadingMessage: string;
    selectedItem: TriggerActionSelectedItem;
    triggers: ITrigger[];
    triggerActions: TriggerAction[];

    private _actionService: IActionStoreService;
    private _config: IApplicationConfiguration;
    private _triggerService: ITriggerStoreService;
    private _triggerActionService: ITriggerActionStoreService;
    private _unsubscribe: () => void;

    static $inject = [
        '$scope',
        '$state',
        '$translate',
        '$rootScope',
        'currentApplicationService',
        'storeService',
        'loggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$state: angular.ui.IStateService,
        private _$translate: angular.translate.ITranslateService,
        private _$rootScope: IRootScope,
        private _currentApplicationService: ICurrentApplicationService,
        private _storeService: IStoreService,
        private _loggerService: ILoggerService
    ) {
        this.triggers = [];
        this.actions = [];
        this.triggerActions = [];
    }

    $onInit() {
        this._triggerService = this._storeService.dispatchers().triggers;
        this._actionService = this._storeService.dispatchers().actions;
        this._triggerActionService = this._storeService.dispatchers().triggerActions;
        this._config = this._currentApplicationService.getApplicationConfiguration();
        this.isEditMode = this._$rootScope.isEditMode;
        this._$scope.$on(toggleEditMode, (event, data) => {
            this.isEditMode = this._$rootScope.isEditMode;
        });

        this.isLoading = true;
        this._unsubscribe = this._storeService.register(
            store => this.updateStore(store),
            [SE.Actions, SE.Groups, SE.TriggerActions, SE.Triggers]
        );
    }

    $onDestroy(): void {
        this._unsubscribe();
    }

    onDrop(data: { itemId: string }) {
        const { itemId } = data;
        let scheduleTrigger: ScheduleTrigger;
        let action: IAction;
        let qualityIssueTrigger: QualityIssueTrigger;

        switch (itemId) {
            case 'scheduleTrigger':
                scheduleTrigger = this.createScheduleTrigger();
                this.triggers = this.sortTriggers(this.triggers.concat([scheduleTrigger]), this.triggerActions);
                this.selectedItem = {
                    type: TriggerActionType.Trigger,
                    trigger: scheduleTrigger
                };
                break;
            case 'reprocessingTaskAction':
                action = {
                    name: this._$translate.instant('Reprocessing task'),
                    isDisabled: false,
                    type: ActionType.ReprocessingTask,
                    options: {
                        hasCleaningInputData: false,
                        hasCleaningWideResults: false,
                        hasAcquisition: true,
                        hasScriptComputation: true,
                        hasComputation: true,
                        hasCalculatedValuesStatuses: true,
                        hasReconciliation: true,
                        hasHistorianStorage: true,
                        hasGenerateEvents: false,
                        includeDependencies: false,
                        includeChildren: true,
                        includeDependent: true,
                        onlyFailed: false,
                        onlyCompleted: false,
                        alignedOn: PeriodReference.End
                    },
                    guid: '',
                    groupGuid: ''
                } as ReprocessingTaskAction;
                this.selectedItem = this.addNewAction(action);
                break;
            case 'sendMailAction':
                action = {
                    name: this._$translate.instant('E-mail'),
                    isDisabled: false,
                    type: ActionType.SendMail,
                    guid: '',
                    dateFormat: '',
                    emailDefinition: {
                        body: '',
                        connectionPointGuid: '',
                        from: '',
                        recipients: { approvalLevels: [], roles: [], users: [], userGroups: [], keywords: [] },
                        subject: '',
                        isExportToChildApplication: false
                    }
                } as SendMailAction;
                this.selectedItem = this.addNewAction(action);
                break;
            case 'qualityIssueTrigger':
                qualityIssueTrigger = this.createQualityIssueTrigger();
                this.triggers = this.sortTriggers(this.triggers.concat([qualityIssueTrigger]), this.triggerActions);
                this.selectedItem = {
                    type: TriggerActionType.Trigger,
                    trigger: qualityIssueTrigger
                };
                break;
            case 'alarmTrigger':
                const alarmTrigger = this.createAlarmTrigger();
                this.triggers = this.sortTriggers(this.triggers.concat([alarmTrigger]), this.triggerActions);
                this.selectedItem = {
                    type: TriggerActionType.Trigger,
                    trigger: alarmTrigger
                };
                break;
            default:
                this._loggerService.logError('Unknown itemId ' + itemId);
                break;
        }
    }

    private addNewAction(action: IAction): TriggerActionSelectedItem {
        const newList = this.actions.concat([action]);
        const dic = {};
        newList.forEach(a => {
            let guid = a.guid;
            if (!a.guid) {
                guid = guidEmpty;
            }
            dic[guid] = a;
        });
        this.actions = this.sortActions(this.triggers, dic, this.triggerActions);
        return {
            type: TriggerActionType.Action,
            action
        };
    }

    private updateStore(store: Store) {
        const { actions, groups, triggers, triggerActions } = store;
        // values are sorted by alpha already
        this.triggerActions = triggerActions.list;
        const triggerList = _.values(triggers.dictionary);
        this.triggers = this.sortTriggers(triggerList, this.triggerActions);
        this.actions = this.sortActions(this.triggers, actions.dictionary, this.triggerActions);
        if (this.selectedItem) {
            switch (this.selectedItem.type) {
                case TriggerActionType.Trigger:
                    this.selectedItem = {
                        type: this.selectedItem.type,
                        trigger: triggers.dictionary[this.selectedItem.trigger.guid]
                    };
                    break;
                case TriggerActionType.Action:
                    this.selectedItem = {
                        type: this.selectedItem.type,
                        action: actions.dictionary[this.selectedItem.action.guid]
                    };
                    break;
            }
        }
        this.groups = _.values(groups.dictionary);
        this.isLoading = false;
    }

    private sortActions(
        triggers: ITrigger[],
        actions: { [guid: string]: IAction },
        triggerActions: TriggerAction[]
    ): IAction[] {
        const actionList = _.values(actions);
        let sortedActions = actionList.filter(t => !triggerActions.filter(ta => ta.actionGuid === t.guid).length)
            .sort((a, b) => a.name.localeCompare(b.name));

        triggers.forEach(t => {
            const localList = [];
            triggerActions.filter(ta => ta.triggerGuid === t.guid)
                .forEach(ta => {
                    if (sortedActions.indexOf(actions[ta.actionGuid]) < 0
                        && localList.indexOf(actions[ta.actionGuid]) < 0) {
                        localList.push(actions[ta.actionGuid]);
                    }
                });
            localList.sort((a, b) => a.name.localeCompare(b.name));
            sortedActions = sortedActions.concat(localList);
        });

        return sortedActions;
    }

    private sortTriggers(triggerList: ITrigger[], triggerActions: TriggerAction[]) {
        const orphanTriggers = triggerList.filter(t => !triggerActions.filter(ta => ta.triggerGuid === t.guid).length)
            .sort((a, b) => a.name.localeCompare(b.name));
        const others = triggerList.filter(t => orphanTriggers.indexOf(t) < 0)
            .sort((a, b) => a.name.localeCompare(b.name));
        return orphanTriggers.concat(others);
    }

    private createScheduleTrigger(): ScheduleTrigger {
        return {
            name: this._$translate.instant('Schedule'),
            type: TriggerType.Schedule,
            frequency: {
                interval: this._config.interval,
                intervalTimeUnit: this._config.intervalTimeUnit
            },
            offset: {
                interval: 0,
                intervalTimeUnit: TimeUnit.Minute
            },
            isDisabled: false,
            guid: '',
            hasLaunchMissedTasks: false
        };
    }

    private createQualityIssueTrigger(): QualityIssueTrigger {
        return {
            name: this._$translate.instant('Quality issue'),
            type: TriggerType.QualityIssue,
            guid: '',
            groupTypes: [],
            groupGuids: [],
            isDisabled: false,
            qualityIssues: []
        };
    }

    private createAlarmTrigger(): AlarmTrigger {
        return {
            alarmLevels: [],
            alarmCategories: [],
            groupGuids: [],
            groupTypes: [],
            guid: '',
            isDisabled: false,
            name: this._$translate.instant('Alarm'),
            type: TriggerType.Alarm
        };
    }

    onItemClick(item: TriggerActionSelectedItem, $event: angular.IAngularEvent) {
        if (this.formController && this.formController.$dirty) {
            this._loggerService.logWarning(this._$translate.instant('Save or cancel changes before selecting another element.'));
        } else {
            if (item) {
                this.selectedItem = item;
                try {
                    if (!this._$scope.$$phase) {
                        // generates error
                        this._$scope.$apply();
                    }
                } catch (e) {
                    console.error(e);
                }
            } else {
                this.selectedItem = null;
            }
        }
        if ($event && $event.stopPropagation) {
            $event.stopPropagation();
        }
    }

    onSave() {
        return this.saveItem(this.selectedItem)
            .then(item => {
                this.selectedItem = item;
                // must return undefined
            });
    }

    private saveItem(item: TriggerActionSelectedItem): angular.IPromise<TriggerActionSelectedItem> {
        let promise: angular.IPromise<TriggerActionSelectedItem>;
        switch (item.type) {
            case TriggerActionType.Trigger:
                const tp = item.trigger.guid
                    ? this._triggerService.updateOne(item.trigger)
                    : this._triggerService.addOne(item.trigger);
                promise = tp
                    .then(data => {
                        this._loggerService.logSuccess(this._$translate.instant('Trigger successfully saved.'));
                        const result: TriggerActionSelectedItem = {
                            type: TriggerActionType.Trigger,
                            trigger: data
                        };
                        return result;
                    })
                    .catch(() => {
                        this._loggerService.logError(this._$translate.instant('Failed to save trigger.'));
                        return item;
                    });
                break;
            case TriggerActionType.Action:
                const ap = item.action.guid
                    ? this._actionService.updateOne(item.action)
                    : this._actionService.addOne(item.action);
                promise = ap
                    .then(data => {
                        this._loggerService.logSuccess(this._$translate.instant('Action successfully saved.'));
                        return {
                            type: TriggerActionType.Action,
                            action: data
                        };
                    })
                    .catch(() => {
                        this._loggerService.logError(this._$translate.instant('Failed to save action.'));
                        return item;
                    });
                break;
            case TriggerActionType.TriggerActionLink:
                const tal = this._triggerActionService.updateOne(item.link);
                promise = tal
                    .then(data => {
                        this._loggerService.logSuccess(this._$translate.instant('Link successfully saved.'));
                        return {
                            type: TriggerActionType.TriggerActionLink,
                            link: data
                        };
                    })
                    .catch(() => {
                        this._loggerService.logError(this._$translate.instant('Failed to save link.'));
                        return item;
                    });
                break;
        }
        return promise;
    }

    onCancel() {
        if (this.selectedItem.type === TriggerActionType.Trigger) {
            if (!this.selectedItem.trigger.guid) {
                const triggers = this.triggers.map(d => d);
                _.remove(triggers, t => t.guid === this.selectedItem.trigger.guid);
                this.triggers = triggers;
            }
        } else if (this.selectedItem.type === TriggerActionType.Action) {
            if (!this.selectedItem.action.guid) {
                const actions = this.actions.map(d => d);
                _.remove(actions, t => t.guid === this.selectedItem.action.guid);
                this.actions = actions;
            }
        }
        this.selectedItem = null;
    }

    onConfigure(item: TriggerActionSelectedItem) {
        if (!this.formController || !this.formController.$dirty) {
            if (item.type === TriggerActionType.Action) {
                this._$state.go('editSendMailAction', { actionGuid: item.action.guid });
            }
        }
    }

    onDeleteClick(item: TriggerActionSelectedItem, $event: angular.IAngularEvent) {
        if (!this.formController || !this.formController.$dirty) {
            const { type } = item;
            $(`#delete${TriggerActionType[type]}Modal`).modal('show');
        }
    }

    onStartStopClick(item: TriggerActionSelectedItem) {
        this.saveItem(item);
    }

    onDeleteTrigger(item: ITrigger) {
        const guid = item.guid;
        return this._triggerService.deleteOne(guid)
            .then(data => {
                this._loggerService.logSuccess(this._$translate.instant('Trigger successfully deleted.'));
                this.selectedItem = null;
            })
            .catch(() => this._loggerService.logError(this._$translate.instant('Failed to delete trigger.')));
    }

    onDeleteAction(item: IAction) {
        const guid = item.guid;
        return this._actionService.deleteOne(guid)
            .then(data => {
                this._loggerService.logSuccess(this._$translate.instant('Action successfully deleted.'));
                this.selectedItem = null;
            })
            .catch(() => this._loggerService.logError(this._$translate.instant('Failed to delete action.')));
    }

    onCreateLink(triggerGuid: string, actionGuid: string) {
        if (!_.some(this.triggerActions, ta => ta.triggerGuid === triggerGuid && ta.actionGuid === actionGuid)) {
            const newItem: TriggerAction = {
                triggerGuid, actionGuid, snooze: { interval: 0, intervalTimeUnit: TimeUnit.Second }
            };
            this._triggerActionService.addOne(newItem)
                .then(data => {
                    this._loggerService.logSuccess(this._$translate.instant('Link successfully added.'));
                    this.selectedItem = {
                        type: TriggerActionType.TriggerActionLink,
                        link: data
                    };
                })
                .catch(() => {
                    this._loggerService.logError(this._$translate.instant('Failed to add link.'));
                });
        }
    }

    onDeleteLink(item: TriggerAction) {
        return this._triggerActionService.deleteOne(item)
            .then(data => {
                this._loggerService.logSuccess(this._$translate.instant('Link successfully deleted.'));
                this.selectedItem = null;
            })
            .catch(() => this._loggerService.logError(this._$translate.instant('Failed to delete link.')));
    }
}

export const wtTriggerActionState: ComponentDefinition = {
    componentId: 'wtTriggerActionState',
    controller: TriggerActionStateController,
    template: require('./triggerActionState.component.html'),
    controllerAs: 'vm',
    bindings: {

    }
};